/**
 * Accueil du menu : reprendre la dernière partie, en créer une, parcourir les serveurs,
 * rejoindre avec un code, et la liste courte de mes parties.
 */
import { useEffect } from 'preact/hooks';
import { ChevronRight, Compass, Gauge, KeyRound, LogIn, Mail, Mountain, Play, Plus, Smile, Users, X, type LucideIcon } from 'lucide-preact';
import type { ServerSummary } from '@snowchain/shared';
import { lobby } from './lobby';
import { menu, openServer } from './state';
import { act, join, joinState } from './actions';
import { AvatarStack, Btn, LiveTag, Spinner, Stars, VisibilityTag, gameDate, initials, relTime, toneOf } from './ui';

function roleLabel(s: ServerSummary): string {
  return s.myRole === 'station' ? 'Gérant de la station' : s.myRole === 'observer' ? 'Observateur' : 'Jamais joué';
}

function ContinueCard(props: { s: ServerSummary }) {
  const s = props.s;
  const d = gameDate(s.gameTime);
  const st = joinState(s);
  const busy = menu.joining.value === s.id;
  return (
    <section class="mn-continue">
      <div class="mn-continue-crest" style={{ background: toneOf(s.id) }} aria-hidden="true">
        {initials(s.stationName || s.name)}
      </div>
      <div class="mn-continue-main">
        <p class="mn-kicker">Reprendre</p>
        <h2 class="mn-continue-title">{s.stationName || s.name}</h2>
        <div class="mn-continue-meta">
          <span>{s.name}</span>
          <VisibilityTag v={s.visibility} />
          <LiveTag live={s.live} />
        </div>
        <div class="mn-continue-meta mn-muted">
          Jour {d.day} · {d.label} · {roleLabel(s)} · joué {relTime(s.lastPlayedAt)}
        </div>
        <div class="mn-chips">
          <span class="mn-chip" title="Réputation de la station">
            <Stars value={s.reputation * 5} />
          </span>
          <span class="mn-chip">
            <Smile size={13} /> {s.satisfaction} %
          </span>
          <span class="mn-chip">
            <Users size={13} /> {s.visitorsToday.toLocaleString('fr-FR')} forfaits
          </span>
          <span class="mn-chip">
            <Mountain size={13} /> {s.openKm.toLocaleString('fr-FR')} km · {s.lifts} remontées
          </span>
        </div>
      </div>
      <div class="mn-continue-side">
        <div class="mn-continue-players">
          <AvatarStack people={s.online} size={26} />
          <span class="mn-muted">
            {s.online.length}/{s.maxPlayers} joueurs
          </span>
        </div>
        <Btn kind="primary" size="lg" icon={Play} disabled={!!st.blocked || busy} title={st.blocked ?? undefined} onClick={() => join(s)}>
          {busy ? 'Connexion...' : st.blocked ? st.label : 'Continuer'}
        </Btn>
        <button type="button" class="mn-link" onClick={() => openServer(s.id)}>
          Détails de la partie <ChevronRight size={14} />
        </button>
      </div>
    </section>
  );
}

function FirstStation() {
  return (
    <section class="mn-continue mn-first">
      <div class="mn-continue-crest" aria-hidden="true">
        <Mountain size={34} />
      </div>
      <div class="mn-continue-main">
        <p class="mn-kicker">Première saison</p>
        <h2 class="mn-continue-title">Ouvrez votre station</h2>
        <p class="mn-muted">
          Deux remontées, trois pistes, un village au pied du domaine et 320 000 € pour commencer. Choisissez si la vallée est ouverte à tous, à vos amis, ou sur invitation.
        </p>
      </div>
      <div class="mn-continue-side">
        <Btn kind="primary" size="lg" icon={Plus} onClick={() => (menu.dialog.value = { k: 'create' })}>
          Créer ma station
        </Btn>
      </div>
    </section>
  );
}

function Tile(props: { icon: LucideIcon; title: string; text: string; onClick: () => void; badge?: number }) {
  const Icon = props.icon;
  return (
    <button type="button" class="mn-tile" onClick={props.onClick}>
      <span class="mn-tile-ic">
        <Icon size={20} />
      </span>
      <span class="mn-tile-text">
        <span class="mn-tile-title">
          {props.title}
          {!!props.badge && <span class="mn-badge">{props.badge}</span>}
        </span>
        <span class="mn-tile-desc">{props.text}</span>
      </span>
      <ChevronRight size={16} class="mn-tile-go" />
    </button>
  );
}

export function WorldRow(props: { s: ServerSummary }) {
  const s = props.s;
  const d = gameDate(s.gameTime);
  const st = joinState(s);
  return (
    <div class="mn-wrow">
      <span class="mn-wrow-crest" style={{ background: toneOf(s.id) }} aria-hidden="true">
        {initials(s.stationName || s.name)}
      </span>
      <button type="button" class="mn-wrow-main" onClick={() => openServer(s.id)}>
        <span class="mn-wrow-title">{s.stationName || s.name}</span>
        <span class="mn-wrow-sub">
          {s.name} · Jour {d.day} · {roleLabel(s)}
        </span>
      </button>
      <LiveTag live={s.live} />
      <span class="mn-wrow-players" title="Joueurs connectés">
        <Users size={13} /> {s.online.length}/{s.maxPlayers}
      </span>
      <Btn kind="secondary" size="sm" icon={Play} disabled={!!st.blocked || menu.joining.value === s.id} title={st.blocked ?? undefined} onClick={() => join(s)}>
        Jouer
      </Btn>
    </div>
  );
}

function InviteBanner() {
  const invites = lobby.social.value.invites;
  const i = invites[0];
  if (!i) return null;
  return (
    <section class="mn-banner" aria-live="polite">
      <span class="mn-banner-ic">
        <Mail size={18} />
      </span>
      <div class="mn-banner-text">
        <b>{i.from.name}</b> vous invite sur <b>{i.world.stationName || i.world.name}</b>
        <span class="mn-muted">
          {' '}
          · {i.world.name} · {relTime(i.at)}
          {invites.length > 1 ? ` · ${invites.length - 1} autre${invites.length > 2 ? 's' : ''} invitation${invites.length > 2 ? 's' : ''}` : ''}
        </span>
      </div>
      <Btn kind="primary" icon={LogIn} onClick={() => join(i.world.id)}>
        Rejoindre
      </Btn>
      <Btn kind="ghost" icon={X} title="Ignorer l'invitation" onClick={() => void act({ op: 'invite.dismiss', id: i.id })} />
    </section>
  );
}

export function HomeView() {
  useEffect(() => lobby.watchServers(), []);
  const me = lobby.me.value;
  const servers = lobby.servers.value;
  const mine = (servers ?? []).filter((s) => s.myRole).sort((a, b) => (b.lastPlayedAt ?? 0) - (a.lastPlayedAt ?? 0));
  const last = mine[0];
  const open = (servers ?? []).filter((s) => !s.myRole).length;
  const stats = lobby.stats.value;
  return (
    <div class="mn-home">
      <header class="mn-hero">
        <p class="mn-kicker">Val Cendrée · saison d’hiver</p>
        <h1>{last ? `Bon retour, ${me?.name ?? ''}` : `Bienvenue, ${me?.name ?? ''}`}</h1>
        <p class="mn-lead">Chaque partie est une vallée en ligne : votre station, ses fournisseurs et les joueurs qui vous rejoignent. Le temps n’y avance que lorsque quelqu’un est connecté.</p>
      </header>
      <InviteBanner />
      {servers === null ? (
        <div class="mn-continue mn-loading">
          <Spinner /> Chargement de vos parties...
        </div>
      ) : last ? (
        <ContinueCard s={last} />
      ) : (
        <FirstStation />
      )}
      <div class="mn-tiles">
        <Tile icon={Plus} title="Nouvelle partie" text="Une nouvelle station, publique, entre amis ou privée." onClick={() => (menu.dialog.value = { k: 'create' })} />
        <Tile
          icon={Compass}
          title="Parcourir les serveurs"
          text={`${open} partie${open > 1 ? 's' : ''} à rejoindre · ${stats.playersOnline} joueur${stats.playersOnline > 1 ? 's' : ''} en ligne`}
          badge={(servers ?? []).filter((s) => s.invitedBy).length}
          onClick={() => {
            menu.filter.value = 'all';
            menu.view.value = 'servers';
          }}
        />
        <Tile icon={KeyRound} title="Rejoindre avec un code" text="Collez le code ou le lien reçu d’un ami." onClick={() => (menu.dialog.value = { k: 'code' })} />
      </div>
      {mine.length > 1 && (
        <section class="mn-section">
          <div class="mn-section-head">
            <h3>Mes parties</h3>
            <button
              type="button"
              class="mn-link"
              onClick={() => {
                menu.filter.value = 'mine';
                menu.view.value = 'servers';
              }}
            >
              Toutes ({mine.length}) <ChevronRight size={14} />
            </button>
          </div>
          <div class="mn-rows">
            {mine.slice(1, 5).map((s) => (
              <WorldRow key={s.id} s={s} />
            ))}
          </div>
        </section>
      )}
      <p class="mn-foot mn-muted">
        <Gauge size={12} /> {stats.worldsLive} partie{stats.worldsLive > 1 ? 's' : ''} en cours · {lobby.status.value === 'online' ? `${Math.round(lobby.rtt.value)} ms` : 'connexion...'}
      </p>
    </div>
  );
}
